import { useState } from 'react';
import { Link } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useCampaigns } from '@/hooks/useCampaigns';
import { formatRelativeDate } from '@/utils/date';
import type { Campaign } from '@/types/api';
import { NewCampaignModal } from '@/components/modals/NewCampaignModal';
import { EditCampaignDetailsModal } from '@/components/modals/EditCampaignDetailsModal';

const FILTER_TABS = ['All Campaigns', 'Active', 'Archived'];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function campaignStatus(_c: Campaign): 'Active' | 'Draft' | 'Archived' {
  return 'Active';
}

const STATUS_BADGE: Record<string, string> = {
  Active: 'aspectly:bg-primary aspectly:text-on-primary',
  Draft: 'aspectly:bg-stone-200 aspectly:text-on-surface-variant',
  Archived: 'aspectly:bg-surface-container-high aspectly:text-on-surface-variant',
};

function SkeletonCard() {
  return (
    <div className="aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:p-6 aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse">
      <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
        <div className="aspectly:w-12 aspectly:h-12 aspectly:rounded-xl aspectly:bg-surface-container-high" />
        <div className="aspectly:flex-1 aspectly:space-y-2">
          <div className="aspectly:h-4 aspectly:w-56 aspectly:bg-surface-container-high aspectly:rounded" />
          <div className="aspectly:h-3 aspectly:w-32 aspectly:bg-surface-container aspectly:rounded" />
        </div>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const orgId = useOrgId();
  const [activeFilter, setActiveFilter] = useState('All Campaigns');
  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const { data, loading, error, refetch } = useCampaigns();

  const campaigns = data ?? [];

  const visible = campaigns.filter((c) => {
    if (activeFilter === 'All Campaigns') return true;
    const s = campaignStatus(c);
    if (activeFilter === 'Active') return s === 'Active' || s === 'Draft';
    if (activeFilter === 'Archived') return s === 'Archived';
    return true;
  });

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:space-y-10">
      {/* Header */}
      <div className="aspectly:flex aspectly:items-end aspectly:justify-between aspectly:gap-6 aspectly:flex-wrap">
        <div className="aspectly:space-y-1">
          <h1 className="aspectly:text-4xl aspectly:font-black aspectly:tracking-tight aspectly:text-on-surface aspectly:font-headline">
            Campaigns
          </h1>
          <p className="aspectly:text-on-surface-variant aspectly:font-medium">
            Manage and organise your creative workflows.
          </p>
        </div>
        <button
          onClick={() => setIsNewCampaignOpen(true)}
          className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:bg-primary aspectly:hover:bg-primary-dim aspectly:text-on-primary aspectly:px-6 aspectly:py-3 aspectly:rounded-xl aspectly:font-bold aspectly:transition-all aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:active:scale-95 aspectly:cursor-pointer"
        >
          <span className="material-symbols-outlined">add</span>
          Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:flex-wrap">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={`aspectly:px-5 aspectly:py-2 aspectly:rounded-full aspectly:text-xs aspectly:font-bold aspectly:tracking-wider aspectly:uppercase aspectly:transition-colors aspectly:cursor-pointer ${
              activeFilter === tab
                ? 'aspectly:bg-primary aspectly:text-on-primary aspectly:shadow-md aspectly:shadow-primary/10' : 'aspectly:bg-surface-container-lowest aspectly:text-on-surface-variant aspectly:hover:bg-primary-container aspectly:border aspectly:border-outline-variant/10'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* API error */}
      {error && (
        <div className="aspectly:mb-8 aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {error}
        </div>
      )}

      {/* Timeline */}
      <div className="aspectly:relative aspectly:max-w-4xl">
        <div
          className="aspectly:absolute aspectly:left-[7px] aspectly:top-4 aspectly:bottom-4 aspectly:w-[2px] aspectly:opacity-20 aspectly:hidden aspectly:md:block"
          style={{
            backgroundImage: 'linear-gradient(to bottom, #8e84ad 50%, transparent 50%)',
            backgroundSize: '2px 8px',
            backgroundRepeat: 'repeat-y',
          }}
        />

        <div className="aspectly:space-y-8">
          {loading &&
            [1, 2, 3].map((i) => (
              <div key={i} className="aspectly:relative aspectly:pl-0 aspectly:md:pl-10">
                <div className="aspectly:hidden aspectly:md:block aspectly:absolute aspectly:left-0 aspectly:top-1.5 aspectly:w-4 aspectly:h-4 aspectly:rounded-full aspectly:bg-surface-container-high aspectly:animate-pulse" />
                <SkeletonCard />
              </div>
            ))}

          {!loading &&
            visible.map((c) => {
              const status = campaignStatus(c);
              return (
                <div key={c.id} className="aspectly:relative aspectly:pl-0 aspectly:md:pl-10">
                  <div
                    className={`aspectly:hidden aspectly:md:block aspectly:absolute aspectly:left-0 aspectly:top-1.5 aspectly:w-4 aspectly:h-4 aspectly:rounded-full aspectly:z-10 ${
                      status === 'Active'
                        ? 'aspectly:bg-primary aspectly:shadow-[0_0_0_6px_rgba(142,132,173,0.15)]' : 'aspectly:bg-surface-container-lowest aspectly:border-2 aspectly:border-primary'
                    }`}
                  />
                  <div className="aspectly:mb-3">
                    <span className="aspectly:text-[10px] aspectly:font-black aspectly:tracking-[0.2em] aspectly:uppercase aspectly:text-primary">
                      {formatRelativeDate(c.updated_at)}
                    </span>
                  </div>
                  <Link
                    to={orgUiPath(orgId, `/campaigns/${c.id}`)}
                    className="aspectly:group aspectly:block aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:p-6 aspectly:border aspectly:border-outline-variant/10 aspectly:hover:border-primary/20 aspectly:hover:shadow-md aspectly:transition-all aspectly:duration-300"
                  >
                    <div className="aspectly:flex aspectly:flex-col aspectly:md:flex-row aspectly:md:items-center aspectly:justify-between aspectly:gap-4">
                      <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
                        <div className="aspectly:w-12 aspectly:h-12 aspectly:rounded-xl aspectly:bg-primary-container aspectly:flex aspectly:items-center aspectly:justify-center">
                          <span
                            className="material-symbols-outlined aspectly:text-primary"
                            style={{ fontVariationSettings: "'FILL' 1" }}
                          >
                            campaign
                          </span>
                        </div>
                        <div>
                          <h3 className="aspectly:text-lg aspectly:font-bold aspectly:text-on-surface aspectly:group-hover:text-primary aspectly:transition-colors aspectly:font-headline">
                            {c.name}
                          </h3>
                          <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:mt-1">
                            <span
                              className={`aspectly:px-2 aspectly:py-0.5 aspectly:text-[9px] aspectly:font-bold aspectly:tracking-wider aspectly:uppercase aspectly:rounded ${STATUS_BADGE[status]}`}
                            >
                              {status}
                            </span>
                            <span className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-medium">
                              Updated {formatRelativeDate(c.updated_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="aspectly:flex aspectly:items-center aspectly:gap-8 aspectly:px-4 aspectly:md:border-l aspectly:border-outline-variant/10">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditingCampaign(c);
                          }}
                          className="aspectly:text-on-surface-variant aspectly:hover:text-primary aspectly:transition-colors aspectly:cursor-pointer"
                        >
                          <span className="material-symbols-outlined">more_vert</span>
                        </button>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}

          {!loading && visible.length === 0 && !error && (
            <div className="aspectly:pl-10 aspectly:py-16 aspectly:text-center">
              <span className="material-symbols-outlined aspectly:text-5xl aspectly:text-primary/30">campaign</span>
              <p className="aspectly:mt-4 aspectly:text-on-surface-variant aspectly:font-medium">No campaigns yet.</p>
              <button
                onClick={() => setIsNewCampaignOpen(true)}
                className="aspectly:mt-4 aspectly:text-primary aspectly:font-bold aspectly:text-sm aspectly:hover:underline aspectly:cursor-pointer"
              >
                Create your first campaign →
              </button>
            </div>
          )}
        </div>
      </div>

      <NewCampaignModal
        isOpen={isNewCampaignOpen}
        onClose={() => setIsNewCampaignOpen(false)}
        onCampaignCreated={() => refetch()}
      />
      <EditCampaignDetailsModal
        isOpen={editingCampaign != null}
        onClose={() => setEditingCampaign(null)}
        campaign={editingCampaign}
        onSaved={() => {
          refetch();
          setEditingCampaign(null);
        }}
      />
    </div>
  );
}
